import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { ROLE_LABELS, type MeResponse } from "@dm/shared";
import { api, NotLoggedInError } from "./api.js";
import { TranscriptPage } from "./TranscriptPage.js";
import { TablePage } from "./TablePage.js";
import { MapPage } from "./MapPage.js";
import { CharacterPage } from "./CharacterPage.js";
import { GameProvider, useGame } from "./game.js";
import { RollPrompts } from "./Dice.js";
import { AwardPopups, LootPrompts } from "./Rewards.js";
import { HomebrewPage, WikiView } from "./Lore.js";
import { AdventureBanner, AdventuresPage } from "./Adventures.js";
import { HelpButton } from "./Help.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { ImagePopup, ImagesPage } from "./Images.js";
import { AmbientBox, AmbientDriver, DmThinking } from "./Ambient.js";
import { SchedulePage } from "./Schedule.js";
import { AppContext, useApp, type AppCtx } from "./appContext.js";
import { CampaignsPage } from "./CampaignsPage.js";
import { CampaignPage } from "./CampaignPage.js";
import { AdminPage } from "./AdminPage.js";
import { InvitePage } from "./InvitePage.js";
import { LiveBanner } from "./LiveBanner.js";
import { MemoryPage } from "./MemoryPage.js";
import { PrivatePage } from "./PrivatePage.js";
import { HostPage } from "./HostPage.js";
import { SettingsPage, settingsTab } from "./SettingsPage.js";
import { CapMeter, CostsPage, SessionCostPopup, SystemBanners } from "./CostsPage.js";

const CharacterWizard = lazy(() => import("./Wizard.js").then((m) => ({ default: m.CharacterWizard })));
import { PlaytestPage, PlaytestPrompts } from "./Playtest.js";

const LOGIN_ERRORS: Record<string, string> = {
  not_in_guild: "Your Discord account is not a member of this campaign's server.",
  invalid_state: "Login expired or was tampered with. Please try again.",
  access_denied: "Discord login was cancelled.",
};

/** Where to go after Discord login (e.g. an invite link opened while logged out). */
const PENDING_HASH = "dm.pendingHash";

type Route =
  | { page: "table" }
  | { page: "character"; id?: number }
  | { page: "wizard" }
  | { page: "transcript" }
  | { page: "memory" }
  | { page: "wiki"; id?: number }
  | { page: "homebrew" }
  | { page: "adventures"; id?: number }
  | { page: "images" }
  | { page: "schedule" }
  | { page: "private" }
  | { page: "host" }
  | { page: "campaigns" }
  | { page: "campaign" }
  | { page: "admin" }
  | { page: "settings" }
  | { page: "costs" }
  | { page: "playtest" }
  | { page: "map" }
  | { page: "invite"; token: string };

function parseRoute(hash: string): Route {
  const inv = /^#\/invite\/([\w-]+)/.exec(hash);
  if (inv) return { page: "invite", token: inv[1]! };
  const m = /^#\/(\w+)(?:\/(\d+))?/.exec(hash);
  switch (m?.[1]) {
    case "wizard":
      return { page: "wizard" };
    case "character":
      return { page: "character", id: m[2] ? Number(m[2]) : undefined };
    case "wiki":
      return { page: "wiki", id: m[2] ? Number(m[2]) : undefined };
    case "adventures":
      return { page: "adventures", id: m[2] ? Number(m[2]) : undefined };
    case "homebrew":
    case "images":
    case "schedule":
    case "transcript":
    case "memory":
    case "private":
    case "host":
    case "campaigns":
    case "campaign":
    case "admin":
    case "settings":
    case "costs":
    case "playtest":
    case "map":
      return { page: m[1] };
    default:
      return { page: "table" };
  }
}

function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}

export function App() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [state, setState] = useState<"loading" | "anon" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);

  const loginError = new URLSearchParams(window.location.search).get("login_error");

  const reloadMe = useCallback(async () => {
    try {
      const m = await api.me();
      setMe(m);
      setState("ready");
      return m;
    } catch (err) {
      if (err instanceof NotLoggedInError) setState("anon");
      else setState((s) => (s === "ready" ? s : "error"));
      return null;
    }
  }, []);

  useEffect(() => {
    void reloadMe().then((m) => {
      if (!m) return;
      const pending = localStorage.getItem(PENDING_HASH);
      if (pending) {
        localStorage.removeItem(PENDING_HASH);
        if (!window.location.hash || window.location.hash === "#/" || window.location.hash === "#/table") window.location.hash = pending;
      }
    });
  }, [reloadMe]);

  const fail = useCallback((err: unknown) => setError(err instanceof Error ? err.message : String(err)), []);
  const ctx = useMemo<AppCtx | null>(() => (me ? { me, reloadMe, fail } : null), [me, reloadMe, fail]);

  if (state === "loading") return <div className="center">Loading…</div>;
  if (state === "error") return <div className="center">The server is unreachable. Try again shortly.</div>;

  if (state === "anon" || !me || !ctx) return <LoginPage error={loginError} />;

  return (
    <AppContext.Provider value={ctx}>
      <div className="page">
        <header>
          <h1>
            {me.campaign?.name ?? "AI Dungeon Master"}
            {me.role && <span className={`tag ${me.role === "player" ? "" : "host"}`}>{ROLE_LABELS[me.role]}</span>}
            {me.campaign && !me.role && me.isAdmin && <span className="tag">Admin view</span>}
            <a className="switch-link" href="#/campaigns">
              {me.campaign ? "Switch campaign" : "Campaigns"}
            </a>
          </h1>
          <div className="user">
            {me.isAdmin && (
              <a className="small" href="#/admin">
                Admin
              </a>
            )}
            <a className="small" href="#/settings/xai">
              Settings
            </a>
            {me.user.avatarUrl && <img src={me.user.avatarUrl} alt="" />}
            <span>{me.user.displayName}</span>
            <button
              onClick={async () => {
                await api.logout();
                window.location.href = "/";
              }}
            >
              Log out
            </button>
          </div>
        </header>
        {error && (
          <div className="toast" onClick={() => setError(null)}>
            {error}
          </div>
        )}
        {me.campaign ? (
          <GameProvider
            key={me.campaign.id}
            campaign={me.campaign}
            user={me.user}
            role={me.role}
            isAdmin={me.isAdmin}
            onCampaignChanged={reloadMe as () => void}
          >
            <Shell />
          </GameProvider>
        ) : (
          <NoCampaign />
        )}
        <ErrorBoundary name="help panel" compact>
          <HelpButton />
        </ErrorBoundary>
      </div>
    </AppContext.Provider>
  );
}

/** No campaign selected yet: only the account-level pages. */
function NoCampaign() {
  const route = useRoute();
  if (route.page === "admin") return <AdminPage />;
  if (route.page === "settings") return <SettingsPage tab={settingsTab(window.location.hash)} />;
  if (route.page === "invite") return <InvitePage token={route.token} />;
  if (route.page === "adventures") return <AdventuresPage key={route.id ?? "list"} id={route.id} />;
  return <CampaignsPage />;
}

function Shell() {
  const route = useRoute();
  const { me } = useApp();
  const { campaign, myCharacter, error, clearError, combat, isHost, session } = useGame();
  const tab = (page: Route["page"], href: string, label: string) => (
    <a href={href} className={route.page === page || (page === "character" && route.page === "wizard") ? "active" : ""}>
      {label}
    </a>
  );
  return (
    <>
      <nav className="tabs">
        {tab("table", "#/table", combat ? "Table ⚔" : "Table")}
        {campaign.battleMap !== false && tab("map", "#/map", "Map")}
        {tab("character", "#/character", myCharacter ? myCharacter.name : "My character")}
        {tab("private", "#/private", "Private")}
        {tab("transcript", "#/transcript", "Transcript")}
        {tab("wiki", "#/wiki", "Wiki")}
        {tab("schedule", "#/schedule", "Schedule")}
        {tab("images", "#/images", "Images")}
        {tab("memory", "#/memory", "Memory")}
        {tab("homebrew", "#/homebrew", "Homebrew")}
        {(isHost || me.canHost || me.isAdmin) && tab("adventures", "#/adventures", "Adventures")}
        {isHost && tab("host", "#/host", session?.paused ? "Host ⏸" : "Host")}
        {isHost && tab("costs", "#/costs", "Costs")}
        {isHost && tab("playtest", "#/playtest", "Playtest")}
        {tab("campaign", "#/campaign", isHost ? "Campaign settings" : "Campaign")}
        {route.page === "campaigns" && tab("campaigns", "#/campaigns", "Campaigns")}
        {route.page === "admin" && me.isAdmin && tab("admin", "#/admin", "Admin")}
        {route.page === "settings" && tab("settings", "#/settings/xai", "Settings")}
        <PlaytestPrompts />
      </nav>
      {error && (
        <div className="toast" onClick={clearError}>
          {error}
        </div>
      )}
      <SystemBanners />
      <SessionCostPopup />
      <RollPrompts />
      <LootPrompts />
      <AwardPopups />
      <ImagePopup />
      <AmbientDriver />
      <DmThinking />
      <ErrorBoundary name="page" resetKey={window.location.hash}>
      {(route.page === "table" || route.page === "character" || route.page === "wizard") && <LiveBanner />}
      {(route.page === "table" || route.page === "host") && <CapMeter />}
      {route.page === "table" && <AdventureBanner />}
      {route.page === "table" && <AmbientBox />}
      {route.page === "table" && <TablePage />}
      {route.page === "map" && <MapPage />}
      {route.page === "character" && <CharacterPage key={route.id ?? "mine"} characterId={route.id} />}
      {route.page === "wizard" && <Suspense fallback={<p className="muted">Loading the wizard…</p>}><CharacterWizard /></Suspense>}
      {route.page === "transcript" && <TranscriptPage campaign={campaign} />}
      {route.page === "memory" && <MemoryPage key={campaign.id} />}
      {route.page === "wiki" && <WikiView key={campaign.id} id={route.id} />}
      {route.page === "homebrew" && <HomebrewPage key={campaign.id} />}
      {route.page === "adventures" && <AdventuresPage key={route.id ?? "list"} id={route.id} />}
      {route.page === "images" && <ImagesPage key={campaign.id} />}
      {route.page === "schedule" && <SchedulePage key={campaign.id} />}
      {route.page === "private" && <PrivatePage key={campaign.id} />}
      {route.page === "host" && (isHost ? <HostPage key={campaign.id} /> : <main className="muted">Only the Host and Co-hosts can open the Host panel.</main>)}
      {route.page === "campaigns" && <CampaignsPage />}
      {route.page === "campaign" && <CampaignPage key={campaign.id} />}
      {route.page === "admin" && <AdminPage />}
      {route.page === "settings" && <SettingsPage tab={settingsTab(window.location.hash)} />}
      {route.page === "costs" && <CostsPage />}
      {route.page === "playtest" && <PlaytestPage key={campaign.id} />}
      {route.page === "invite" && <InvitePage token={route.token} />}
      </ErrorBoundary>
    </>
  );
}

/** Logged out: Discord login. Email magic links only show if the server turns them back on (EMAIL_LOGIN_ENABLED; deferred). */
function LoginPage({ error }: { error: string | null }) {
  const invite = /^#\/invite\/([\w-]+)/.exec(window.location.hash)?.[1] ?? null;
  const [emailOn, setEmailOn] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => void api.authOptions().then((o) => setEmailOn(o.email), () => undefined), []);
  const remember = () => {
    if (window.location.hash && window.location.hash !== "#/") localStorage.setItem(PENDING_HASH, window.location.hash);
  };
  return (
    <div className="center">
      <h1>AI Dungeon Master</h1>
      <p>{invite ? "You've been invited to a campaign. Log in to join." : "Sign in with the Discord account you use at the table."}</p>
      {error && <p className="error">{LOGIN_ERRORS[error] ?? `Login failed (${error}).`}</p>}
      <a className="button" href="/auth/discord/login" onClick={remember}>
        Log in with Discord
      </a>
      {emailOn && (
        <div className="email-login">
          <p className="muted">or get a login link by email{invite ? "" : " (for accounts that added an email)"}</p>
          {sent ? (
            <p className="note">{sent}</p>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setBusy(true);
                setErr(null);
                remember();
                try {
                  setSent((await api.emailLoginStart(email.trim(), invite ?? undefined)).message);
                } catch (x) {
                  setErr(x instanceof Error ? x.message : String(x));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <input type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              <button className="primary" disabled={busy || !email.includes("@")}>Email me a link</button>
            </form>
          )}
          {err && <p className="error">{err}</p>}
          <p className="muted small">Voice play needs Discord: email-only accounts can link Discord later in Settings → Account.</p>
        </div>
      )}
    </div>
  );
}
